<script setup lang="ts">
import { ref, computed } from 'vue';
import type { OpportunityType } from '@apparatix/types/Leads';
import DetailEditorControls from '@apparatix/library/DetailEditorControls.vue';
import OpportunityDetailView from './OpportunityDetailView.vue';
import OpportunityDetailForm from './OpportunityDetailForm.vue';
import {
  useOpportunityDetails,
  useOpportunityDetailEditor,
  useToastNotifications,
} from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunities } from '@apparatix/composables';
import OpportunityLostDialog from './OpportunityLostDialog.vue';

type Props = {
  type: OpportunityType;
}
const props = defineProps<Props>();
const store = useOpportunityStore();
const detail = useOpportunityDetails(props.type);
const { showSuccess } = useToastNotifications();

const selectedOpportunity = store.selectedOpportunity[props.type].opportunity;

const userList = store.options[props.type].users;
// List does not include suspended users, but we need to recognize existing suspended owner
if (!userList.some(item => item.id === selectedOpportunity.ownerId)) {
  userList.push({
    fullName: selectedOpportunity.owner,
    id: selectedOpportunity.ownerId,
    officeId: 0,
    userId: '',
    isActive: false,
  });
  userList.sort((a, b) => a.fullName.localeCompare(b.fullName));
}

const selections = {
  owner: userList,
  status: store.options[props.type].availableStatuses,
  temperature: store.options[props.type].temperatures,
  office: store.options[props.type].offices,
  confidence: store.options[props.type].confidenceIntervals,
  interval: store.options[props.type].campaignIntervals,
  allStatuses: store.options[props.type].possibleStatuses,
  sources: store.options[props.type].accountSources.map(source => source.selection),
};

const {
  anticipatedStart,
  budget,
  confidence,
  dueDate,
  errors,
  interval,
  meta,
  name,
  office,
  opportunityType,
  owner,
  periods,
  rate,
  resetForm,
  status,
  temperature,
  value,
  values,
  viewComponentValues,
  opportunityLostReasons,
  source,
} = useOpportunityDetailEditor(props.type, selections, store.selectedOpportunity[props.type].opportunity, store.selectedOpportunity[props.type].revenueData);

const isEditing = ref(false);
const isStatusLockedByContract = ref(false);

const handleEditClick = async () => {
  if (props.type === 'Opportunity') {
    isStatusLockedByContract.value = await getOpportunityHasContract(selectedOpportunity.id);
  } else {
    isStatusLockedByContract.value = false;
  }

  isEditing.value = true;
};

const handleCancelClick = () => {
  isEditing.value = false;
  resetForm();
};

const emit = defineEmits<{
  (e: 'handleContentUpdate'): void;
}>();

const isRevenueProjectionChanged = () => {
  if (props.type === 'Lead') {
    return false;
  }

  const revenueData = store.selectedOpportunity[props.type].revenueData;
  const opportunityData = store.selectedOpportunity[props.type].opportunity;
  if (periods.value !== revenueData.periods) {
    return true;
  }
  if (interval.value !== revenueData.interval) {
    return true;
  }
  if (rate.value !== revenueData.rate) {
    return true;
  }
  if (Number(confidence.value.confidencePercentage) !== opportunityData.ci) {
    return true;
  }
  if (owner.value.id !== opportunityData.ownerId) {
    return true;
  }
  if (anticipatedStart.value !== opportunityData.anticipatedStart) {
    return true;
  }
  if (office.value !== opportunityData.office) {
    return true;
  }

  return false;
};

const handleSaveClick = async () => {
  await detail.sendOppUpdate(values);
  if (isRevenueProjectionChanged()) {
    emit('handleContentUpdate');
  }

  // Mapping to get our numbers the user selected for lost reasons without having to wait for endpoint to reply with them
  if (props.type === 'Opportunity' && opportunityLostReasons.value.every(item => typeof item === 'number')) {
    opportunityLostReasons.value = opportunityLostReasons.value.map(id => {
      const match = store.options[props.type].opportunityLostReasons.find(item => item.id === id);
      return match ? match.tag : null;
    });
  }

  showSuccess(`${props.type} Saved`, 'Success');
  isEditing.value = false;
  resetForm({ values });
};

const showOpportunityLostDialog = ref(false);

const onConfirmOpportunityLost = (reasonsSet) => {
  showOpportunityLostDialog.value = false;
  opportunityLostReasons.value = reasonsSet;
};

const onCancelOpportunityLost = () => {
  detail.status.value = store.options[props.type].availableStatuses.find(statusObj => statusObj.status === store.selectedOpportunity[props.type].opportunity.status);
  status.value = store.options[props.type].availableStatuses.find(statusObj => statusObj.status === store.selectedOpportunity[props.type].opportunity.status);
  showOpportunityLostDialog.value = false;
};

const onStatusChange = (statusSet) => {
  if (props.type === 'Opportunity') {
    if (statusSet.state === 'completed') {
      showOpportunityLostDialog.value = true;
    } else {
      opportunityLostReasons.value = [];
    }
  }
};

const { isOpportunityCompleted, getOpportunityHasContract } = useOpportunities();
const disableStatusChange = computed(() => isStatusLockedByContract.value || isOpportunityCompleted(store.selectedOpportunity[props.type]?.opportunity));
</script>
<template>
  <DetailEditorControls
    header="Details"
    :is-editing="isEditing"
    :disable-save="Boolean(Object.keys(errors).length) || !meta.dirty"
    @on:edit-click="handleEditClick"
    @on:cancel-click="handleCancelClick"
    @on:save-click="handleSaveClick"
  >
    <OpportunityDetailView v-if="!isEditing" :data="viewComponentValues" :selections="selections" />
    <OpportunityDetailForm
      v-if="isEditing"
      v-model:anticipated-start="anticipatedStart"
      v-model:budget="budget"
      v-model:campaign-interval="interval"
      v-model:confidence="confidence"
      v-model:due-date="dueDate"
      v-model:name="name"
      v-model:office="office"
      v-model:owner="owner"
      v-model:periods="periods"
      v-model:rate="rate"
      v-model:status="status"
      v-model:temperature="temperature"
      v-model:type="opportunityType"
      v-model:source="source"
      v-model:value="value"
      :disable-status-change="disableStatusChange"
      :errors="errors"
      :selections="selections"
      @on:status-change="onStatusChange"
    />
  </DetailEditorControls>
  <OpportunityLostDialog
    :type="props.type"
    :visible="showOpportunityLostDialog"
    @close="onCancelOpportunityLost"
    @on:confirm="onConfirmOpportunityLost"
  />
</template>
